import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import type { ApiAppointment, CalendarPrintSource, PrintDay, PrintDocument } from '../types';
import { formatPrintRow } from './formatPrintRow';

dayjs.extend(utc);
dayjs.extend(timezone);

export const PRINT_PRODUCT_NAME = 'Healthera Connect';
export const CONFIDENTIALITY_NOTICE =
  'Contains patient identifiable information. Store securely and dispose of confidentially.';
export const GENERATED_AT_FORMAT = 'D MMM YYYY, h:mm A';
const DAY_LABEL_FORMAT = 'dddd D MMMM YYYY';

export function incompleteListBanner(printed: number, total: number): string {
  return `INCOMPLETE LIST: showing appointments 1-${printed} of ${total}. Narrow the list with a filter and print again.`;
}

function dayLabel(dayKey: string): string {
  return dayjs(dayKey).format(DAY_LABEL_FORMAT).toUpperCase();
}

interface BuildPrintDocumentInput {
  source: Pick<CalendarPrintSource, 'title' | 'subtitle' | 'activeFilters'>;
  rows: ApiAppointment[];
  total: number;
  truncated: boolean;
  pharmacyName: string;
  pharmacyTimezone: string;
  generatedBy: string;
  now: Date;
}

/** Pure: rows in, the sheet's data out. Grouped by day so a one-day and a week print share one shape. */
export function buildPrintDocument({
  source,
  rows,
  total,
  truncated,
  pharmacyName,
  pharmacyTimezone,
  generatedBy,
  now,
}: BuildPrintDocumentInput): PrintDocument {
  const sorted = [...rows].sort(
    (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
  );

  const days = new Map<string, PrintDay>();
  sorted.forEach((appointment) => {
    const row = formatPrintRow(appointment, { pharmacyTimezone, now });
    const day = days.get(row.dayKey) ?? { key: row.dayKey, label: dayLabel(row.dayKey), rows: [] };
    day.rows.push(row);
    days.set(row.dayKey, day);
  });

  return {
    pharmacyName,
    title: source.title,
    subtitle: source.subtitle,
    activeFilters: source.activeFilters,
    total,
    printed: sorted.length,
    truncated,
    generatedAt: dayjs(now).tz(pharmacyTimezone).format(GENERATED_AT_FORMAT),
    generatedBy,
    days: [...days.values()].sort((a, b) => a.key.localeCompare(b.key)),
  };
}
